import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Store, MessageCircle, Settings, FileText } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useLayout } from '@/contexts/LayoutContext';
import { useUserDevice } from '@/hooks/useUserDevice';

const BottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serial } = useEffectiveSerial();
  const { isBottomNavVisible } = useLayout();
  const { currentUserIdentity } = useUserDevice();
  
  // Self-managed users don't need the Requests tab
  const isSelfManaged = currentUserIdentity === 'self-managed';

  // Don't show navigation on device selector page or when explicitly hidden
  if (!isBottomNavVisible || location.pathname === '/device-selector') {
    return null;
  }

  const allTabs = [
    {
      id: 'support',
      label: 'Support',
      icon: MessageCircle,
      path: `/${serial}/support`,
      isActive: location.pathname === `/${serial}/support`
    },
    {
      id: 'appstore',
      label: 'Apps',
      icon: Store,
      path: `/${serial}`,
      isActive: location.pathname === `/${serial}` || location.pathname === '/'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      path: `/${serial}/settings`,
      isActive: location.pathname.startsWith(`/${serial}/settings`)
    },
    {
      id: 'requests',
      label: 'Requests',
      icon: FileText,
      path: `/${serial}/requests`,
      isActive: location.pathname === `/${serial}/requests`
    }
  ];

  // Filter out Requests tab for self-managed users
  const tabs = isSelfManaged 
    ? allTabs.filter(tab => tab.id !== 'requests')
    : allTabs;

  return (
    <nav 
      className="fixed bottom-4 left-4 right-4 bg-background/70 backdrop-blur-2xl border border-white/20 rounded-[22px] shadow-[0_8px_32px_rgba(0,0,0,0.15),inset_0_1px_1px_rgba(255,255,255,0.1)] z-40"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="flex items-center justify-around h-[56px] max-w-lg mx-auto px-4">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = tab.isActive;
          
          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className="flex flex-col items-center justify-center min-w-[56px] h-full transition-all duration-100 active:scale-[0.95] active:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-lg"
              aria-label={tab.label}
              role="tab"
              aria-selected={active}
            >
              <div className="relative">
                <Icon 
                  className={`w-[22px] h-[22px] transition-colors ${
                    active 
                      ? 'text-primary' 
                      : 'text-muted-foreground'
                  }`} 
                  strokeWidth={active ? 2.5 : 1.5}
                  fill={active ? 'currentColor' : 'none'}
                  aria-hidden="true"
                />
              </div>
              <span className={`text-[10px] mt-0.5 font-medium transition-colors ${
                active 
                  ? 'text-primary' 
                  : 'text-muted-foreground'
              }`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;
